import React, { useState } from "react";
import { Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import { friendlyAuthError } from "../../utils/friendlyAuthError";

export default function CustomerAuth() {
  const { showToast } = useToast();
  // "signin" | "signup" | "forgot"
  const [mode, setMode] = useState("signin");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot-password transient state. `resetSent` flips to true after the
  // reset email is dispatched so the form swaps to a confirmation panel.
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  async function handleSignIn(e) {
    e.preventDefault();
    if (!email || !password) {
      showToast("Please enter your email and password.", "error");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // Auth state change in useAuth will handle the rest
    } catch (err) {
      showToast(friendlyAuthError(err.message) || "Sign in failed.", "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    if (!fullName.trim()) {
      showToast("Please enter your full name.", "error");
      return;
    }
    if (!email || !password) {
      showToast("Please enter your email and password.", "error");
      return;
    }
    if (password.length < 6) {
      showToast("Password must be at least 6 characters.", "error");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;

      const userId = data.user?.id;
      if (!userId) throw new Error("Sign up succeeded but no user ID returned.");

      // Email confirmation is required — user must confirm before profile setup.
      // Note: Supabase intentionally returns success with a fake user object
      // when the email already exists (anti-enumeration), so we can't reliably
      // distinguish "new signup" from "duplicate email" client-side. Copy
      // covers both cases without leaking which one happened.
      if (!data.session) {
        showToast(
          "Check your email to confirm your account. If you already have one, try signing in instead.",
          "info"
        );
        return;
      }

      // Try the RPC first. Fall back to a direct insert if the function is
      // missing or the role column doesn't exist yet on the profiles table.
      const { error: rpcError } = await supabase.rpc("create_customer_account", {
        p_user_id: userId,
        p_full_name: fullName.trim(),
      });

      if (rpcError) {

        const { error: insertError } = await supabase
          .from("profiles")
          .insert({ id: userId, full_name: fullName.trim(), role: "customer" });

        if (insertError) {
          // Both paths failed. Sign out so the user isn't stuck on the
          // "Finishing account setup" screen with no way out.
          await supabase.auth.signOut();
          throw new Error(
            "Account setup failed — your sign-in was undone. " +
              "Please ask the admin to check Supabase (profiles table / create_customer_account function). " +
              `Detail: ${rpcError.message}`
          );
        }
      }

      // Profile now exists. Refresh the JWT so onAuthStateChange fires again
      // and useAuth re-reads the profile with role = 'customer'.
      await supabase.auth.refreshSession();

      showToast("Account created! You are now signed in.", "success");
    } catch (err) {
      showToast(friendlyAuthError(err.message), "error");
    } finally {
      setLoading(false);
    }
  }

  // Forgot-password submit — sends a Supabase recovery email. When the
  // recipient clicks the link they return to this origin with a recovery
  // hash, which App.js detects and routes to ResetPasswordPage.
  async function handleForgotSubmit(e) {
    e.preventDefault();
    const target = (resetEmail || "").trim();
    if (!target) {
      showToast("Please enter your email address.", "error");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(target, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      showToast(friendlyAuthError(err.message) || "Could not send reset email.", "error");
    } finally {
      setLoading(false);
    }
  }

  function switchMode(nextMode) {
    setMode(nextMode);
    // Reset the forgot-password UI whenever we leave that mode so a stale
    // "check your email" confirmation doesn't leak into a fresh attempt.
    setResetSent(false);
    setResetEmail("");
    setShowPassword(false);
  }

  return (
    <div className="two-grid">
      <Card className="hero dark">
        <h1>Track your quotes</h1>
        <p>
          Create a free account to submit job requests, receive quotes from
          local tradies, and accept or decline in one place.
        </p>
        <p className="muted">Already received a quote link? You don't need an account — just open the link.</p>
      </Card>

      <Card>
        <h2>
          {mode === "signin"
            ? "Sign in"
            : mode === "forgot"
            ? "Reset your password"
            : "Create account"}
        </h2>

        {mode === "forgot" ? (
          resetSent ? (
            <>
              <p className="muted">
                Check your email for a password reset link. Click it and you'll
                come back here to set a new password.
              </p>
              <div className="button-row">
                <Button secondary onClick={() => switchMode("signin")}>
                  Back to sign in
                </Button>
              </div>
            </>
          ) : (
            <form onSubmit={handleForgotSubmit}>
              <p className="muted" style={{ marginBottom: "0.75rem" }}>
                Enter the email on your account and we'll send you a link to
                set a new password.
              </p>
              <div className="form-group">
                <label className="form-label">
                  <Mail size={14} /> Email
                </label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="button-row">
                <Button type="submit" disabled={loading}>
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
                <Button
                  type="button"
                  secondary
                  onClick={() => switchMode("signin")}
                  disabled={loading}
                >
                  Back to sign in
                </Button>
              </div>
            </form>
          )
        ) : (
          <form onSubmit={mode === "signin" ? handleSignIn : handleSignUp}>
            {mode === "signup" && (
              <div className="form-group">
                <label className="form-label">
                  <User size={14} /> Full name
                </label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Jane Smith"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                <Mail size={14} /> Email
              </label>
              <input
                className="form-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Lock size={14} /> Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  className="form-input"
                  type={showPassword ? "text" : "password"}
                  placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* Forgot-password link — shown on the sign-in view only so
                  the signup flow stays focused. Uses the existing
                  .link-btn style; no new CSS. */}
              {mode === "signin" && (
                <p className="muted small" style={{ marginTop: "6px" }}>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => switchMode("forgot")}
                  >
                    Forgot password?
                  </button>
                </p>
              )}
            </div>

            <div className="button-row">
              <Button type="submit" disabled={loading}>
                {loading
                  ? mode === "signin"
                    ? "Signing in..."
                    : "Creating account..."
                  : mode === "signin"
                  ? "Sign in"
                  : "Create account"}
              </Button>
            </div>
          </form>
        )}

        {mode !== "forgot" && (
          <p className="muted" style={{ marginTop: "1rem", textAlign: "center" }}>
            {mode === "signin" ? (
              <>
                Don't have an account?{" "}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => switchMode("signup")}
                >
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => switchMode("signin")}
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        )}
      </Card>
    </div>
  );
}
